import React, { useContext } from 'react';
import { IconButton, TextField, useTheme, Tooltip } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import errorResponses from '../../errorResponses';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { IGetFieldDataTypeAndConfig } from './utils/FieldTypes';
import { getUpdatedFieldData } from './utils/getUpdatedFieldData';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { refreshCertification } from '../../utils/certificationCache';
import FieldValidationsModal from './FieldsSettings/FieldValidationsModal';
import { IField } from '../../types/my-types';

import FieldValidationIcons from './FieldsSettings/FieldValidationsIcons';

export interface FieldLabelProps {
  fieldId: string;
  fieldLabel: string;
  getFieldDataTypeAndConfig: IGetFieldDataTypeAndConfig;
  trainingSetId: string;
  page: number;
  field: IField;
}

const FieldLabel = (props: FieldLabelProps) => {
  const theme = useTheme();
  const { fieldId, field, fieldLabel, getFieldDataTypeAndConfig, trainingSetId, page } = props;

  const { setToast } = useContext(AppContext);
  const glynt = useGlynt();
  const queryClient = useQueryClient();

  const { mutate: updateField, isPending } = useMutation({
    mutationFn: async (params: any) => (await glynt).fields.update(params.id, params.data),

    onSuccess: () => {
      refreshCertification(queryClient, trainingSetId);
      setToast({
        open: true,
        message: 'Field Renamed.',
        variant: 'success',
      });

      queryClient.invalidateQueries({
        queryKey: ['fields', page, trainingSetId]
      });
    },

    onError: async (error) => {
      setToast({
        open: true,
        message: errorResponses.failedToRenameField,
        variant: 'error',
      });
    }
  });

  const { mutate: deleteField, isPending: isDeleteMutationLoading } = useMutation({
    mutationFn: async (id: string) => (await glynt).fields.del(id),

    onError: (err, _, context: any) => {
      console.log(err);
      setFeedback({
        open: true,
        message: 'Sorry, failed to delete field.',
      });
      queryClient.setQueryData(['fields', page, trainingSetId], context.previousFields);
    },

    onSuccess: () => {
      refreshCertification(queryClient, trainingSetId);
      setToast({
        open: true,
        message: 'Field Deleted.',
        variant: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: ['fields', page, trainingSetId]
      });
    }
  });

  const { setFeedback } = React.useContext(AppContext);
  const [value, setValue] = React.useState(fieldLabel);

  const ref = React.useRef(null);

  // TODO: use mui confirm box
  // sometime it takes a while for api to delete field, try opt ui
  const confirmDelete = () => {
    if (
      window.confirm(
        'It will delete any marked data related to this field. Are you sure to delete this field?'
      )
    ) {
      deleteField(fieldId);
    }
  };

  const save = async () => {
    if (value.length === 0) {
      confirmDelete();
    } else if (fieldLabel !== value) {
      try {
        const { dataType, dataTypeConfig } = getFieldDataTypeAndConfig(fieldId);
        const updatedFieldData = getUpdatedFieldData({
          label: value,
          fieldDataType: dataType,
          fieldDataTypeConfig: dataTypeConfig,
        });

        await updateField({
          id: fieldId,
          data: updatedFieldData,
        });
      } catch (error) {
        alert('Sorry, failed to save');
      }
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <TextField
        autoComplete="off"
        fullWidth
        id={fieldLabel}
        inputRef={ref}
        name={fieldLabel}
        inputProps={{ style: { color: value !== fieldLabel ? theme.palette.primary.main : '' } }}
        disabled={isPending || isDeleteMutationLoading}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-cy={fieldLabel}
        style={{
          cursor: isPending || isDeleteMutationLoading ? 'disabled' : 'pointer',
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            save();
          }
        }}
      />
      <div
        style={{ display: 'flex', width: '100px', alignItems: 'center', justifyContent: 'center' }}
      >
        <IconButton
          style={{
            padding: 4,
            cursor: 'pointer',
            opacity: isPending || isDeleteMutationLoading ? 0.5 : 1,
          }}
          id="check"
          data-testid="check"
          data-cy={`btn-check-field-${fieldLabel}`}
          disabled={isPending || isDeleteMutationLoading || value === fieldLabel}
          onClick={() => {
            save();
          }}
        >
          <CheckCircle />
        </IconButton>
        <FieldValidationIcons
          validationConfig={field.validation_config}
          hasFixedWidth={true}
          fieldLabel={fieldLabel}
        />
        <FieldValidationsModal field={field} trainingSetId={trainingSetId} />

        <IconButton
          id="trash"
          data-testid="trash"
          data-cy={`btn-trash-field-${fieldLabel}`}
          style={{
            padding: 4,
            cursor: isPending || isDeleteMutationLoading ? 'disabled' : 'pointer',
            opacity: isPending || isDeleteMutationLoading ? 0.5 : 1,
          }}
          onClick={() => {
            confirmDelete();
          }}
        >
          <DeleteIcon />
        </IconButton>
      </div>
    </div>
  );
};

export default FieldLabel;
